import {
  BILLING_PLAN_ORDER,
  type BillingPlanKey,
  getBillingPlan,
} from './plans'
import { getPeriodMonthMultiplier, isPaidBillingPlanKey } from './usage'

export type BillingPlanChangeDirection = 'upgrade' | 'downgrade' | null

export type BillingPeriodSnapshot = {
  planKey: BillingPlanKey
  baseCredits: number
  rolloverCredits: number
  includedEmailSends: number
  creditsUsed: number
  emailsUsed: number
}

export function getBillingPlanChangeDirection(args: {
  currentPlanKey: BillingPlanKey
  nextPlanKey: BillingPlanKey
}): BillingPlanChangeDirection {
  const currentIndex = BILLING_PLAN_ORDER.indexOf(args.currentPlanKey)
  const nextIndex = BILLING_PLAN_ORDER.indexOf(args.nextPlanKey)

  if (currentIndex === -1 || nextIndex === -1 || currentIndex === nextIndex) {
    return null
  }

  return nextIndex > currentIndex ? 'upgrade' : 'downgrade'
}

export function applyPlanChangeToBillingPeriod(args: {
  openPeriod: BillingPeriodSnapshot
  targetPlanKey: BillingPlanKey
}) {
  const direction = getBillingPlanChangeDirection({
    currentPlanKey: args.openPeriod.planKey,
    nextPlanKey: args.targetPlanKey,
  })

  if (direction !== 'upgrade') {
    return {
      direction,
      shouldPatchOpenPeriod: false,
      updatedOpenPeriod: args.openPeriod,
    }
  }

  // The mid-cycle upgrade math (preserve current paid credits, add the new
  // tier on top) only makes sense for paid -> paid transitions. A free open
  // period has no already-paid credits to preserve, and relabeling it to a
  // paid tier would grant paid credits without an invoice. Leave the free
  // period alone and let `applyInvoicePaid` create the paid period cleanly.
  if (!isPaidBillingPlanKey(args.openPeriod.planKey)) {
    return {
      direction,
      shouldPatchOpenPeriod: false,
      updatedOpenPeriod: args.openPeriod,
    }
  }

  const targetPlan = getBillingPlan(args.targetPlanKey)

  return {
    direction,
    shouldPatchOpenPeriod: true,
    updatedOpenPeriod: {
      ...args.openPeriod,
      planKey: args.targetPlanKey,
      baseCredits: args.openPeriod.baseCredits + targetPlan.credits,
      includedEmailSends: Math.max(
        args.openPeriod.includedEmailSends,
        targetPlan.emailSends
      ),
    },
  }
}

export function getScheduledPlanChangeState(args: {
  currentPlanKey: BillingPlanKey
  nextPlanKey: BillingPlanKey | null
  nextPlanEffectiveAt: number | null
}) {
  if (!args.nextPlanKey) {
    return {
      nextPlanKey: null,
      nextPlanName: null,
      nextPlanEffectiveAt: null,
      hasScheduledPlanChange: false,
      scheduledPlanChangeDirection: null,
    }
  }

  const direction = getBillingPlanChangeDirection({
    currentPlanKey: args.currentPlanKey,
    nextPlanKey: args.nextPlanKey,
  })

  // Only a DOWNGRADE is a legitimate scheduled change (the account already
  // holds the lower next-cycle price while the open period stays at the higher
  // tier paid for this cycle). An `upgrade` direction here means the account
  // plan is ABOVE the open period plan — that is alignment DRIFT (a paid org
  // stuck on a lower/free open period), NOT a real "scheduled upgrade". Never
  // render it as a benign scheduled change: it would show a phantom "upgrade
  // scheduled" banner and invite a re-checkout. The reconciler heals the
  // period and the invariant monitor alarms on it.
  if (!direction || direction === 'upgrade') {
    return {
      nextPlanKey: null,
      nextPlanName: null,
      nextPlanEffectiveAt: null,
      hasScheduledPlanChange: false,
      scheduledPlanChangeDirection: null,
    }
  }

  return {
    nextPlanKey: args.nextPlanKey,
    nextPlanName: getBillingPlan(args.nextPlanKey).name,
    nextPlanEffectiveAt: args.nextPlanEffectiveAt,
    hasScheduledPlanChange: true,
    scheduledPlanChangeDirection: direction,
  }
}

export function shouldResetBillingPeriodForInvoice(args: {
  billingReason: string | null | undefined
  hasOpenBillingPeriod: boolean
  /**
   * The current open period's plan and the tier the paid invoice confirms.
   * When BOTH are supplied and the open period sits BELOW the confirmed paid
   * tier (free->paid, or under-tier paid), force a reset regardless of
   * `billingReason`. Without this, a first paid invoice that carries
   * `billing_reason='subscription_update'` (immediate-proration upgrades) would
   * take the no-reset branch and NEVER create the paid period — leaving a
   * paying org stuck on a free/lower open period (the alignment-drift bug).
   */
  openPeriodPlanKey?: BillingPlanKey
  resolvedPlanKey?: BillingPlanKey
}) {
  if (!args.hasOpenBillingPeriod) {
    return true
  }

  if (
    args.openPeriodPlanKey &&
    args.resolvedPlanKey &&
    getBillingPlanChangeDirection({
      currentPlanKey: args.openPeriodPlanKey,
      nextPlanKey: args.resolvedPlanKey,
    }) === 'upgrade'
  ) {
    return true
  }

  return args.billingReason !== 'subscription_update'
}

/**
 * Invoice windows identify the cycle that was paid, even when Stripe has moved
 * to another cycle before delivery. Only a subscription_update proration that
 * ends with the live cycle and starts inside it may use the full live window.
 * This preserves full-cycle upgrade dates without moving historical payments
 * into the current cycle or inventing grants from missing invoice evidence.
 */
export function resolvePaidPeriodWindow(args: {
  billingReason?: string | null
  liveStart: number | undefined
  liveEnd: number | undefined
  invoiceStart: number | undefined
  invoiceEnd: number | undefined
}): { periodStart: number | undefined; periodEnd: number | undefined } {
  if (
    args.billingReason === 'subscription_update' &&
    args.liveStart !== undefined &&
    args.liveEnd !== undefined &&
    args.invoiceStart !== undefined &&
    args.invoiceEnd !== undefined &&
    args.liveStart <= args.invoiceStart &&
    args.invoiceStart < args.invoiceEnd &&
    args.liveEnd === args.invoiceEnd
  ) {
    return { periodStart: args.liveStart, periodEnd: args.liveEnd }
  }
  return { periodStart: args.invoiceStart, periodEnd: args.invoiceEnd }
}

export type AdditiveUpgradeGrant = {
  baseCredits: number
  includedEmailSends: number
  creditsUsed: number
  emailsUsed: number
  costUsedUsd: number
}

/**
 * Compute the ADDITIVE mid-cycle upgrade grant for `applyInvoicePaid`'s reset
 * branch. When a proration (`subscription_update`) invoice lands while the open
 * period is a PAID period BELOW the confirmed tier — i.e. the in-app optimistic
 * sync was lost and the upgrade is arriving via the invoice instead — the fresh
 * paid period must STACK the new tier on the already-paid base AND preserve
 * usage, exactly like {@link applyPlanChangeToBillingPeriod}'s in-app upgrade.
 * Opening a FLAT new-tier period would under-grant the promised
 * `oldBase + newTier`; forgiving usage would over-grant. Returns the grant to
 * carry onto the fresh period, or `null` for the cases that stay FLAT: free→paid
 * (a free base isn't "paid" credit), renewals (`subscription_cycle` — a clean
 * new allowance), downgrades, same-tier, and "no prior open period".
 */
export function computeAdditiveUpgradeGrantForInvoice(args: {
  billingReason: string | null | undefined
  previousOpenPeriod: {
    planKey: BillingPlanKey
    baseCredits: number
    includedEmailSends: number
    creditsUsed: number
    emailsUsed: number
    costUsedUsd: number
  } | null
  resolvedPlanKey: BillingPlanKey
}): AdditiveUpgradeGrant | null {
  if (
    args.billingReason !== 'subscription_update' ||
    !args.previousOpenPeriod ||
    !isPaidBillingPlanKey(args.previousOpenPeriod.planKey) ||
    getBillingPlanChangeDirection({
      currentPlanKey: args.previousOpenPeriod.planKey,
      nextPlanKey: args.resolvedPlanKey,
    }) !== 'upgrade'
  ) {
    return null
  }

  const targetPlan = getBillingPlan(args.resolvedPlanKey)
  return {
    baseCredits: args.previousOpenPeriod.baseCredits + targetPlan.credits,
    includedEmailSends: Math.max(
      args.previousOpenPeriod.includedEmailSends,
      targetPlan.emailSends
    ),
    creditsUsed: args.previousOpenPeriod.creditsUsed,
    emailsUsed: args.previousOpenPeriod.emailsUsed,
    costUsedUsd: args.previousOpenPeriod.costUsedUsd,
  }
}

/**
 * Cheap, IO-free DRIFT detector for the reconciler's candidate scan and the
 * invariant monitor. Returns true when an org's billing is mis-aligned in the
 * "paid account stuck on a lower/free open period" direction.
 *
 * The ONLY legitimate `account != period` state is a scheduled DOWNGRADE
 * (account BELOW period); that is NOT drift and returns false here. A paid
 * account with NO open period is drift (the paid period was never created); a
 * free account with no period is just a fresh free org (not drift).
 */
export function detectPlanAlignmentDrift(args: {
  accountPlanKey: BillingPlanKey
  openPeriodPlanKey: BillingPlanKey | null | undefined
}): boolean {
  if (!args.openPeriodPlanKey) {
    return isPaidBillingPlanKey(args.accountPlanKey)
  }
  return (
    getBillingPlanChangeDirection({
      currentPlanKey: args.openPeriodPlanKey,
      nextPlanKey: args.accountPlanKey,
    }) === 'upgrade'
  )
}

/**
 * Authoritative heal predicate for the reconciler. A drifted candidate is only
 * healed when the org is genuinely entitled to a paid period RIGHT NOW:
 *
 *   - `confirmedPaidTier` is the tier resolved from the LIVE Stripe price
 *     (NEVER defaulted to the account plan or 'free' — a price that maps to no
 *     known plan must arrive here as `null` and block the heal + alarm).
 *   - `hasPaidInvoiceThisCycle` must be true: the current cycle's invoice is
 *     `paid` AND its window covers now. The caller deliberately does NOT gate on
 *     amount, so a 100%-coupon org (a $0 `paid` invoice) is still entitled —
 *     parity with the webhook grant path. The window + active-status checks
 *     reject trialing/incomplete/unpaid subs and stale/upfront invoices, so we
 *     never grant paid credits without a current-cycle payment.
 *   - the open period must be BELOW the confirmed tier (free or under-tier) or
 *     missing. At-or-above is never healed: equal = aligned; above = the
 *     entitled tier of a scheduled downgrade (revoking it would strip paid
 *     entitlement the org already bought this cycle).
 */
export function shouldHealStuckPaidPeriod(args: {
  confirmedPaidTier: BillingPlanKey | null
  hasPaidInvoiceThisCycle: boolean
  openPeriodPlanKey: BillingPlanKey | null | undefined
}): boolean {
  if (
    !args.confirmedPaidTier ||
    !isPaidBillingPlanKey(args.confirmedPaidTier)
  ) {
    return false
  }
  if (!args.hasPaidInvoiceThisCycle) {
    return false
  }
  if (!args.openPeriodPlanKey) {
    return true
  }
  return (
    getBillingPlanChangeDirection({
      currentPlanKey: args.openPeriodPlanKey,
      nextPlanKey: args.confirmedPaidTier,
    }) === 'upgrade'
  )
}

/**
 * Monotonic ordering guard for `customer.subscription.*` webhook deliveries.
 * Stripe gives at-least-once, UNORDERED delivery, so a reordered/duplicate event
 * can arrive carrying older state. Returns true when `incomingEventAt` is
 * STRICTLY older than the last-applied `lastStripeEventAt` (both epoch ms) — the
 * caller then drops the write so stale state can't clobber newer state (e.g. a
 * post-cancel `subscription.updated` resurrecting a canceled org, or a stale
 * redelivered `subscription.deleted` after a resubscribe). Equal timestamps and
 * missing watermarks PROCEED (in-app optimistic syncs stamp `Date.now()` and so
 * win the tie against an older second-granular webhook). Must be applied by
 * EVERY writer in the family (created/updated/deleted) or the watermark gaps.
 */
export function isStaleStripeEvent(
  incomingEventAt: number | undefined,
  lastStripeEventAt: number | undefined
): boolean {
  return (
    incomingEventAt !== undefined &&
    lastStripeEventAt !== undefined &&
    incomingEventAt < lastStripeEventAt
  )
}

/**
 * Account fields whose correct value depends on WHICH `customer.subscription.*`
 * event is newest. Applying a stale event can revert a reactivation back to
 * cancel-at-period-end, flip the status, or change the tier — so these are the
 * fields the same-second tie-breaker protects.
 */
export type StripeOrderingSensitiveState = {
  cancelAtPeriodEnd: boolean
  status: string
  planKey: BillingPlanKey
}

/**
 * Same-SECOND tie-breaker backing up the monotonic {@link isStaleStripeEvent}
 * guard. `event.created` is whole SECONDS, so two `customer.subscription.*`
 * events created in the same second carry EQUAL `stripeEventAt`; the strict-`<`
 * staleness check treats neither as stale, so BOTH apply and whichever is
 * PROCESSED last wins — nondeterministic w.r.t. true Stripe order (it can revert
 * a reactivation back to cancel-at-period-end, flip status, or change the tier).
 * Stripe exposes no sub-second sequence to order them by, so on an EQUAL
 * timestamp we refuse to APPLY a CHANGE to an ordering-sensitive field: the
 * first write at a given second sticks and a conflicting same-second event is
 * dropped + alarmed for human review. Returns true when the caller MUST drop the
 * event.
 *
 * We deliberately do NOT relax the staleness check to `<=`: an in-app optimistic
 * sync stamps `Date.now()` (ms), which is strictly GREATER than a concurrent
 * webhook's floored `event.created*1000`, so it never ties here and is never
 * dropped. Only two same-second WEBHOOK deliveries can tie — exactly the case
 * this guards. A non-conflicting same-second event (no sensitive-field change,
 * e.g. an idempotent redelivery) returns false and proceeds.
 */
export function isConflictingSameSecondStripeEvent(args: {
  incomingEventAt: number | undefined
  lastStripeEventAt: number | undefined
  incoming: StripeOrderingSensitiveState
  existing: StripeOrderingSensitiveState | null | undefined
}): boolean {
  if (
    args.incomingEventAt === undefined ||
    args.lastStripeEventAt === undefined ||
    args.incomingEventAt !== args.lastStripeEventAt ||
    !args.existing
  ) {
    return false
  }
  return (
    args.incoming.cancelAtPeriodEnd !== args.existing.cancelAtPeriodEnd ||
    args.incoming.status !== args.existing.status ||
    args.incoming.planKey !== args.existing.planKey
  )
}

export type FreeFallbackTransition = {
  shouldClosePreviousPeriod: boolean
  newFreePeriod: {
    planKey: 'free'
    baseCredits: number
    rolloverCredits: 0
    includedEmailSends: number
    creditsUsed: 0
    emailsUsed: 0
    costUsedUsd: 0
    periodStart: number
    periodEnd: number
  }
  newAccountSnapshot: {
    planKey: 'free'
    status: 'free'
    cancelAtPeriodEnd: false
    cancelAt: undefined
    stripeSubscriptionId: undefined
    stripePriceId: undefined
    currentPeriodStart: number
    currentPeriodEnd: number
  }
  cachedSummary: {
    includedCredits: number
    rolloverCredits: 0
    creditsUsed: 0
    remainingCredits: number
    includedEmailSends: number
    emailsUsed: 0
    remainingEmailSends: number
  }
}

/**
 * Build the entitlement transition for `customer.subscription.deleted`.
 *
 * Pure: no IO, no Convex. The caller is responsible for actually closing the
 * previous period row, inserting the new free period row, and patching the
 * `billingAccounts` snapshot. Math is centralized here so that:
 *
 *   - free-plan limits come from the canonical {@link getBillingPlan}('free')
 *     catalog (no hardcoded 500 / 1000 anywhere)
 *   - rollover is always 0 — there is no credit rollover anywhere (each period
 *     is capped at the plan allowance); this just makes that explicit for free
 *   - the cached summary numbers used by the nav indicator are derived from
 *     the same plan limits, so the UI updates consistently after deletion
 *
 * `periodWindow` is supplied by the caller (typically via
 * `getFallbackPeriodWindow(Date.now())`) to keep this helper deterministic
 * and trivially testable.
 */
/**
 * Should `applySubscriptionDeleted` PRESERVE the open period instead of
 * dropping the org to free right away?
 *
 * A prepaid commitment is paid through its whole multi-month window, and the
 * checkout disclosure promises "cancellation preserves access through the
 * term". A mid-term delete (admin cancel, dashboard cancel, a portal immediate
 * cancel) must therefore keep the entitlements the org already paid for; the
 * hourly `closeElapsedPaidPeriodsWithoutSubscription` cron lapses the period
 * to free once the window passes.
 *
 * Deliberately narrow, all four required:
 *   - the ACCOUNT carries the commitment marker (ordinary subs never do)
 *   - the deleted sub is the one that OPENED an open period (a duplicate
 *     loser's delete never matches — its period was never the org's)
 *   - the window is still running
 *   - the window is MULTI-month (`getPeriodMonthMultiplier > 1`), so a
 *     month-7+ monthly cycle keeps today's immediate cancel→free semantics
 */
export function shouldPreservePrepaidPeriodOnDelete(args: {
  accountIsGrowthCommitment: boolean | undefined
  deletedSubscriptionId: string | null | undefined
  openPeriods: ReadonlyArray<{
    stripeSubscriptionId?: string
    periodStart: number
    periodEnd: number
  }>
  nowMs: number
}): boolean {
  if (args.accountIsGrowthCommitment !== true) {
    return false
  }
  if (
    typeof args.deletedSubscriptionId !== 'string' ||
    args.deletedSubscriptionId.length === 0
  ) {
    return false
  }

  return args.openPeriods.some(
    (period) =>
      period.stripeSubscriptionId === args.deletedSubscriptionId &&
      period.periodEnd > args.nowMs &&
      getPeriodMonthMultiplier(period.periodStart, period.periodEnd) > 1
  )
}

export function buildFreeFallbackTransition(args: {
  hasOpenBillingPeriod: boolean
  periodWindow: { periodStart: number; periodEnd: number }
}): FreeFallbackTransition {
  const freePlan = getBillingPlan('free')
  return {
    shouldClosePreviousPeriod: args.hasOpenBillingPeriod,
    newFreePeriod: {
      planKey: 'free',
      baseCredits: freePlan.credits,
      rolloverCredits: 0,
      includedEmailSends: freePlan.emailSends,
      creditsUsed: 0,
      emailsUsed: 0,
      costUsedUsd: 0,
      periodStart: args.periodWindow.periodStart,
      periodEnd: args.periodWindow.periodEnd,
    },
    newAccountSnapshot: {
      planKey: 'free',
      status: 'free',
      cancelAtPeriodEnd: false,
      cancelAt: undefined,
      stripeSubscriptionId: undefined,
      stripePriceId: undefined,
      currentPeriodStart: args.periodWindow.periodStart,
      currentPeriodEnd: args.periodWindow.periodEnd,
    },
    cachedSummary: {
      includedCredits: freePlan.credits,
      rolloverCredits: 0,
      creditsUsed: 0,
      remainingCredits: freePlan.credits,
      includedEmailSends: freePlan.emailSends,
      emailsUsed: 0,
      remainingEmailSends: freePlan.emailSends,
    },
  }
}

/**
 * Stripe caches the first response for an idempotency key, including payment
 * failures. Scope plan-change keys to the subscription state being mutated and
 * a one-minute attempt window: simultaneous submits deduplicate, while a user
 * retry after fixing payment gets a fresh execution.
 */
export function buildPlanChangeIdempotencyKey(args: {
  kind: 'change' | 'keep'
  orgId: string
  subscriptionId: string
  fromPriceId: string
  targetPriceId: string
  periodKey: string | number
  nowMs: number
}): string {
  const bucket = Math.floor(args.nowMs / 60_000)
  return `planchange:${args.kind}:${args.orgId}:${args.subscriptionId}:${args.fromPriceId}>${args.targetPriceId}:${args.periodKey}:${bucket}`
}
